import type { Deeplink, Platform } from '@/types';

// Domain configuration
const TRUSTED_DOMAIN = 'klipr.it';
const UNSAFE_DOMAIN = import.meta.env.VITE_UNSAFE_DOMAIN || 'kliprweb.it';

/**
 * Check if a deeplink is trusted (known platform or whitelisted generic)
 * A link is trusted if:
 * - It's a known platform (platform !== 'generic')
 * - OR it's a generic link that has been whitelisted (checkNeeded === false)
 */
export const isDeeplinkTrusted = (
  deeplink: Pick<Deeplink, 'platform' | 'checkNeeded'>
): boolean => {
  // Known platform = trusted
  if (deeplink.platform !== 'generic') return true;
  // checkNeeded === false means whitelisted domain
  return deeplink.checkNeeded === false;
};

/**
 * Get the appropriate domain for a deeplink based on trust level
 */
export const getDomain = (
  deeplink: Pick<Deeplink, 'platform' | 'checkNeeded'>
): string => {
  return isDeeplinkTrusted(deeplink) ? TRUSTED_DOMAIN : UNSAFE_DOMAIN;
};

/**
 * Get the full short URL for a deeplink (with appropriate domain)
 */
export const getShortUrl = (
  deeplink: Pick<Deeplink, 'code' | 'platform' | 'checkNeeded'>
): string => {
  const domain = getDomain(deeplink);
  return `${domain}/${deeplink.code}`;
};

/**
 * Get the full short URL with https:// prefix
 */
export const getShortUrlWithProtocol = (
  deeplink: Pick<Deeplink, 'code' | 'platform' | 'checkNeeded'>
): string => {
  return `https://${getShortUrl(deeplink)}`;
};

/**
 * Get a short URL from just a code and platform info
 * Useful when you don't have the full deeplink object
 */
export const getShortUrlFromPlatform = (
  code: string,
  platform: Platform,
  checkNeeded: boolean = true
): string => {
  const domain = isDeeplinkTrusted({ platform, checkNeeded }) ? TRUSTED_DOMAIN : UNSAFE_DOMAIN;
  return `${domain}/${code}`;
};

// Export constants for use elsewhere if needed
export { TRUSTED_DOMAIN, UNSAFE_DOMAIN };
